import Channel, { ThreadChannel } from '@@types/Channel';
import ChannelMailSetting from '@@types/ChannelMailSetting';
import { CHANNEL_ID, LOG_ID, MEMBER_ID, StringTMap, THREAD_CHANNEL_ID } from '@@types/common';
import Log, { AllThreadLog, InviteMemberLog, ThreadLogInMain } from '@@types/Log';

import { AnimationEffect, LOG_FLAGS, MAIN_THREAD_CHANNEL_ID, SYSTEM_LOG_TYPE } from '~/constants';
import i18n from '~/i18n';
import { isDirectChannel, isPrivateChannel, isThreadChannel, isTopicChannel } from '~/services/channelService';
import notificationService from '~/services/notificationService';
import popupWindowService from '~/services/popupWindowService';
import WebsocketConnector from '~/services/WebsocketConnector';
import Actions from '~/stores/Actions';
import LogActions from '~/stores/actions/LogActions';
import { animationEffectStore } from '~/stores/AnimationEffectStore';
import ChannelLogsStore, { channelLogsStore } from '~/stores/ChannelLogsStore';
import { channelMailSettingStore } from '~/stores/ChannelMailSettingStore';
import { channelManager } from '~/stores/ChannelManager';
import { channelNoticesStore } from '~/stores/ChannelNoticesStore';
import { channelsStore } from '~/stores/ChannelsStore';
import { coachMarkStore } from '~/stores/CoachMarkStore';
import DetailUiState, { mainDetailUiState, threadDetailUiState } from '~/stores/DetailUiState';
import { globalSettingStore } from '~/stores/GlobalSettingStore';
import { api } from '~/stores/providers/Api2';
import environment from '~/stores/providers/environment';
import { searchStore } from '~/stores/SearchStore';
import { slashCommandAppsStore } from '~/stores/SlashCommandAppsStore';
import { threadChannelLogsStore } from '~/stores/ThreadChannelLogsStore';
import { threadChannelStore } from '~/stores/ThreadChannelStore';
import { uiState } from '~/stores/UiState';
import { userStore } from '~/stores/UserStore';
import { isCherryBlossomText } from '~/utils/animationEffectUtil';
import {
  canCreateThreadLog,
  chunkMentionUrlText,
  createInviteMemberLog,
  extractMentionMembersId,
  getPreviewLog,
  getTextByVoIp,
  isCommandLog,
  isEphemeralLog,
  isFileLog,
  isForwardLog,
  isMailLog,
  isReplyLog,
  isStickerLog,
  isSystemLog,
  isVoIpLog,
  verifyRequiredProp,
} from '~/utils/channelLogsUtil';
import { isImageType, isVideo } from '~/utils/fileLogUtil';
import { isNonEmpty } from '~/utils/fnUtil/fnUtil';
import rename from '~/utils/fnUtil/rename';
import { mEither, mMaybe } from '~/utils/monadUtil';
import { isNative } from '~/utils/runEnvironmentUtil';
import { isNotificationAllRange, isNotificationNoneRange, isNotificationOn } from '~/utils/settingUtil';
import { replaceMentionToName, safeParseJsonText } from '~/utils/textUtil/textUtil';
import { isEmpty } from 'lodash';
import { equals, filter, find, mergeRight, objOf, propEq } from 'ramda';
import always from 'ramda/es/always';
import anyPass from 'ramda/es/anyPass';
import cond from 'ramda/es/cond';
import curry from 'ramda/es/curry';
import juxt from 'ramda/es/juxt';
import last from 'ramda/es/last';
import map from 'ramda/es/map';
import omit from 'ramda/es/omit';
import pick from 'ramda/es/pick';
import pipe from 'ramda/es/pipe';
import prop from 'ramda/es/prop';
import propOr from 'ramda/es/propOr';
import reject from 'ramda/es/reject';
import T from 'ramda/es/T';
import when from 'ramda/es/when';
import without from 'ramda/es/without';

type LogCreateParams = {
  shouldRead: boolean;
  isUserLog: boolean;
  hasFocus: boolean;
  showCurrentChannelLog: boolean;
  isCurrentChannelLog: boolean;
  isScrollBottom: boolean;
  hasScrollbar: boolean;
  channel: Channel;
  log: Log;
  detailUiState: DetailUiState;
  channelLogsStore: ChannelLogsStore;
  logActions: LogActions;
};

type LogCreateReferences = { originalLogMap?: StringTMap<Log>; channelMap?: StringTMap<ThreadChannel> };

class LogMessageHandler {
  private setNewLogSeqByNewLog = ({ log, detailUiState, logActions }: LogCreateParams) => {
    const { hasScrollbar, isScrollBottom } = detailUiState.scrollAreaInfo;
    const isScrollTargetLog = detailUiState.scrollTarget === log.id;

    if (hasScrollbar && !isScrollBottom && !isScrollTargetLog) {
      logActions.setNewLog(log);
    }
  };

  private handleNotificationParams = (log: Log, title: string, body: string) => {
    const notiParams = {
      title,
      body,
      channelId: log.channelId,
      onClick: () => {
        if (coachMarkStore.isInInviteChannelCoachMark) {
          this.actions.coachMarkActions.complete();
        }
        this.actions.channelActions.selectChannelByNoti(log.channelId);
        window.focus();
      },
    };
    if (!isNative()) {
      notificationService.notify(notiParams);
      return;
    }
    this.actions.nativeActions.sendNotification(notiParams);
  };

  private getMailTitle = curry((channelId: string, setting: ChannelMailSetting): string => {
    return isNonEmpty(setting?.emailTitle)
      ? setting?.emailTitle
      : this.actions.channelActions.getTitle({ channelId, includeUser: true });
  });

  constructor(private ws: WebsocketConnector, private actions: Actions) {}

  public handleCreate = (log: Log, references: LogCreateReferences) => {
    if ('parentChannelId' in log && log.parentChannelId) {
      this.handleCreateInThread(log, references);
      if (mainDetailUiState.contentsChannelId === MAIN_THREAD_CHANNEL_ID) {
        this.handleCreateInMainThreadChannel(log, references);
      }
      return;
    }
    const isScrollBottom = mainDetailUiState.scrollAreaInfo.isScrollBottom;
    this.setIgnoreUpdateScrollInfo(true, mainDetailUiState);
    const isUserLog = log.senderId === userStore.id;
    const showCurrentChannelLog = log.channelId === mainDetailUiState.contentsChannelId;
    const isCurrentChannelLog = showCurrentChannelLog && uiState.currentContext === 'main';
    const hasFocus = this.actions.nativeActions.hasFocus();
    const shouldRead = (!isUserLog || isSystemLog(log)) && isCurrentChannelLog && hasFocus;
    const channel = channelsStore.getById(log.channelId);
    const params = {
      shouldRead,
      isUserLog,
      hasFocus,
      isCurrentChannelLog,
      showCurrentChannelLog,
      isScrollBottom,
      hasScrollbar: mainDetailUiState.scrollAreaInfo.hasScrollbar,
      channel,
      log,
      detailUiState: mainDetailUiState,
      channelLogsStore,
      logActions: this.actions.logActions,
    };

    if (showCurrentChannelLog) {
      this.handleCreateCurrentChannelLog(params);
      channelLogsStore.mergeReferenceLogMap(references?.originalLogMap);
      if (isUserLog && uiState.openThreadChannelOnCreateLog) {
        uiState.setOpenThreadChannelOnCreateLog(false);
        this.actions.threadChannelActions.safeOpenThreadChannel(log as AllThreadLog);
      }
    }
    juxt<any, any>([this.updateChannelByCreatedLog, this.showChannel, this.showNotification])(params);
    this.setIgnoreUpdateScrollInfo(false, mainDetailUiState);
  };

  private handleCreateInThread = (log: Log, references: LogCreateReferences) => {
    const isScrollBottom = threadDetailUiState.scrollAreaInfo.isScrollBottom;
    this.setIgnoreUpdateScrollInfo(true, threadDetailUiState);
    const isUserLog = log.senderId === userStore.id;
    const showCurrentChannelLog = uiState.threadOpen && log.channelId === threadDetailUiState.contentsChannelId;
    const isCurrentChannelLog = showCurrentChannelLog && uiState.currentContext === 'thread';
    const hasFocus = this.actions.nativeActions.hasFocus();
    const shouldRead = (!isUserLog || isSystemLog(log)) && isCurrentChannelLog && hasFocus;
    const threadChannel = references?.channelMap?.[log.channelId];
    const channel = threadChannelStore.getById(log.channelId) ?? threadChannel;
    const params = {
      shouldRead,
      isUserLog,
      hasFocus,
      showCurrentChannelLog,
      isCurrentChannelLog,
      isScrollBottom,
      channel,
      log,
      hasScrollbar: threadDetailUiState.scrollAreaInfo.hasScrollbar,
      detailUiState: threadDetailUiState,
      channelLogsStore: threadChannelLogsStore,
      logActions: this.actions.threadLogActions,
    };

    if (showCurrentChannelLog) {
      this.handleCreateCurrentThreadChannelLog(params);
      threadChannelLogsStore.mergeReferenceLogMap(references?.originalLogMap);
    }

    threadChannel && threadChannelStore.updateChannel(threadChannel);
    threadChannel &&
      !isSystemLog(log) &&
      this.updateRootLogThreadInfo(threadChannel.parentChannelLogId, {
        channelId: threadChannel.channelId,
        totalCount: threadChannel.totalCount,
        lastUpdatedAt: log.sentAt,
      });

    this.showNotification({ ...params, channel: threadChannelStore.getById(log.channelId) });
    this.setIgnoreUpdateScrollInfo(false, threadDetailUiState);
  };

  private handleCreateInMainThreadChannel = (log: Log, references: LogCreateReferences) => {
    let threadChannel = references?.channelMap?.[log.channelId];

    threadChannelStore.updateChannel(threadChannel);
    if (isSystemLog(log) || !threadChannelStore.isFollow(log.channelId)) {
      return;
    }
    const isFromMe = log.senderId === userStore.id;
    const isCurrentChannelLog =
      uiState.threadOpen &&
      log.channelId === threadDetailUiState.contentsChannelId &&
      uiState.currentContext === 'thread';
    if (!isFromMe) {
      threadChannelStore.updateChannel({
        channelId: log.channelId,
        unreadFlag: uiState.hasFocus && isCurrentChannelLog ? false : log.unreadFlag ?? threadChannel.unreadFlag,
      });
    }
    const index = threadChannelStore.mainThreadChannelIdList.findIndex(equals(log.channelId));
    if (
      index > -1 &&
      threadChannelLogsStore.visibleStartIndex <= index &&
      threadChannelLogsStore.visibleStopIndex >= index
    ) {
      channelLogsStore.mergeReferenceLogMap(references.originalLogMap);
      threadChannelLogsStore.addThreadLogList(log.channelId, log);
    } else if (!isFromMe && !isCurrentChannelLog) {
      uiState.setHasUpdatedThreadChannel(true);
    }
  };

  public handleDelete = (
    log: Log & {
      parentChannelLogId?: LOG_ID;
      lastChannelLogSentAt?: number;
    }
  ) => {
    const hasThreadLog = log.flags === LOG_FLAGS.THREAD_LOG_DELETED;
    if (hasThreadLog) {
      channelLogsStore.updateLog(log);
    } else {
      this.updateChannelByDeleteLog(log);
      if (threadChannelLogsStore.threadPanelParentLogId === log.id) {
        this.actions.threadChannelActions.clearThreadPanel();
      }
      if (mainDetailUiState.contentsChannelId === log.channelId) {
        channelLogsStore.removeLog(log.id);
      } else if (
        threadDetailUiState.contentsChannelId === log.channelId ||
        mainDetailUiState.contentsChannelId === MAIN_THREAD_CHANNEL_ID
      ) {
        threadChannelLogsStore.removeThreadLog(log);
      }
    }
    if (log.parentChannelLogId) {
      this.updateThreadChannelTotalCount(log.channelId, log.totalCount);
      this.updateRootLogThreadInfo(log.parentChannelLogId, {
        totalCount: log.totalCount,
        lastUpdatedAt: log.lastChannelLogSentAt,
      });
    }

    searchStore.deleteSearchedLog(log.id);
    this.deletePostedNotice(log);
  };

  private updateThreadChannelTotalCount = (channelId: THREAD_CHANNEL_ID, totalCount: number) => {
    threadChannelStore.updateChannel({
      channelId,
      totalCount,
    });
  };

  private updateRootLogThreadInfo = (
    rootLogId: LOG_ID,
    thread: { channelId?: CHANNEL_ID; totalCount: number; lastUpdatedAt: number }
  ) => {
    const threadRootLog = channelLogsStore.getById(rootLogId);
    if (!threadRootLog) {
      return;
    }
    const threadCount = (threadRootLog as ThreadLogInMain).thread?.totalCount ?? 0;

    channelLogsStore.updateLog({
      id: rootLogId,
      thread: {
        channelId: thread.channelId || (threadRootLog as ThreadLogInMain).thread?.channelId,
        ...thread,
      },
    });

    // 스레드 최초 로그인 경우
    if (
      !threadCount &&
      rootLogId === channelLogsStore.lastLog?.id &&
      mainDetailUiState.contentsChannelId === threadRootLog.channelId &&
      mainDetailUiState.scrollAreaInfo.isScrollBottom
    ) {
      mainDetailUiState.scrollToBottom();
    }
  };

  public handleUpdate = (log: Log) => {
    const mainChannelId = mainDetailUiState.contentsChannelId;
    if (mainChannelId === log.channelId) {
      this.handleUpdateInner(log, { detailUiState: mainDetailUiState, channelLogsStore });
    } else if (threadDetailUiState.contentsChannelId === log.channelId) {
      this.handleUpdateInner(log, { detailUiState: threadDetailUiState, channelLogsStore: threadChannelLogsStore });
    } else if (threadChannelLogsStore.threadPanelParentLogId === log.id) {
      threadChannelLogsStore.updateParentLog(log);
    }

    if (mainChannelId === MAIN_THREAD_CHANNEL_ID) {
      threadChannelLogsStore.updateLogInMainThreadChannel(log);
    }
  };

  private handleUpdateInner = (
    log: Log,
    { detailUiState, channelLogsStore }: { detailUiState: DetailUiState; channelLogsStore: ChannelLogsStore }
  ) => {
    const lastLog = last(channelLogsStore.logList);
    const isLastLog = lastLog && lastLog.seq === log.seq;
    const needUpdateLog = channelLogsStore.logList.some(({ id }) => log.id === id);
    this.setIgnoreUpdateScrollInfo(true, detailUiState);
    // 번역결과가 내려 올 수 있어서 updateLog 하기 전에 scrollBottom 캐시
    const isScrollBottom = detailUiState.scrollAreaInfo.isScrollBottom;
    needUpdateLog && channelLogsStore.updateLog(log);
    this.updatePostedNotice(log);
    if (isLastLog && isScrollBottom) {
      detailUiState.scrollToBottom();
    } else if (log.senderId === userStore.id) {
      detailUiState.setScrollTargetLog(log);
    }
    this.setIgnoreUpdateScrollInfo(false, detailUiState);
  };

  private handleCreateCurrentChannelLog = (params: LogCreateParams) => {
    juxt<any, any>([
      this.openMeetingCommand,
      this.setLastLogSeq, // 여기까지 읽음 처리
      this.setNewLogSeqByNewLog, // newLogSeq 설정
      this.appendLogOrFetchLogs, // 대화 추가 || 최신 대화 fetch
      this.scrollToBottom, // 스크롤이 아래에 있으면 가장 아래로 이동시킴
      this.animationEffect, // 채널 애니메이션 효과
      this.readLog, // readSeq 업데이트
      this.appendInviteLogInMain, // 초대 로그 생성 -> 스크롤 타겟 지정 -> 초대 로그 추가
      this.calcContentsChannelNotInWork,
      this.openThreadInfoCoachMark,
    ])(params);
  };

  private handleCreateCurrentThreadChannelLog = (params: LogCreateParams) => {
    juxt<any, any>([
      this.openMeetingCommand,
      this.setLastLogSeq, // 여기까지 읽음 처리
      this.setNewLogSeqByNewLog, // newLogSeq 설정
      this.appendLogOrFetchLogs, // 대화 추가 || 최신 대화 fetch
      this.scrollToBottom, // 스크롤이 아래에 있으면 가장 아래로 이동시킴
      this.readLog, // readSeq 업데이트
      this.inviteOrAppendInviteLogInThread, // 초대 로그 생성 -> 스크롤 타겟 지정 -> 초대 로그 추가
    ])(params);
  };

  private updateChannelByCreatedLog = ({ log, shouldRead, channel }: LogCreateParams) => {
    if (!channel) return;
    pipe<any, any, any, any, any, any>(
      rename({ sentAt: 'updatedAt' }),
      when(anyPass([isSystemLog, isEphemeralLog]), omit(['updatedAt'])),
      pick(['mentionCount', 'unreadCount', 'channelId', 'seq', 'updatedAt']),
      when(always(shouldRead), this.setCountsZero),
      channelManager.updateChannel
    )(log);
  };

  private setCountsZero = (log: Log) => {
    return mergeRight(log, { unreadCount: 0, mentionCount: 0 });
  };

  private showChannel = (params: LogCreateParams) => {
    if (params.channel && !params.channel.displayed) {
      api.channel.updateChannelDisplayed(params.channel.channelId, true);
    }
  };

  private showNotification = async (params: LogCreateParams) => {
    if (!this.shouldShowNotification(params) || !params.channel) return;
    const [title, sender, text] = await Promise.all([
      this.getNotificationTitle(params),
      this.getSender(params),
      this.getNotificationText(params),
    ]);

    this.handleNotificationParams(params.log, title, this.joinIfExist(sender, text, ': '));
  };

  private joinIfExist = (value: string, other: string, joinText: string) => {
    return value && other ? `${value}${joinText}${other}` : value || other;
  };

  private openMeetingCommand = ({ log, hasFocus }: LogCreateParams) => {
    const appId = log?.senderId;
    if (hasFocus && slashCommandAppsStore.meetingTriggeredChannel === log.channelId && isCommandLog(log)) {
      switch (appId) {
        case userStore.webexId:
          return this.openWebexWindow(log);
        case userStore.meetingId:
          return this.openMeetingWindow(log);
      }
    }
  };

  private openMeetingWindow = (log: Log) => {
    if (!('attachments' in log)) {
      return;
    }
    slashCommandAppsStore.setMeetingTriggeredChannel(null);
    const attachment = log.attachments[0];
    const isTriggeredByMe = attachment.callbackId === userStore.id;
    const includesPassword = log.text.includes('password');
    if (isTriggeredByMe && !includesPassword) {
      const user = this.actions.userActions.user;
      api.common.getAuthToken(userStore.id).then((result) => {
        popupWindowService.openMeeting(attachment.actions[0].value, user.emailAddress, user.name, result?.token ?? '');
      });
    }
  };

  private openWebexWindow = (log: Log) => {
    slashCommandAppsStore.setMeetingTriggeredChannel(null);
    const userId = userStore.id;
    pipe(
      reject(propEq('type', 'TEXT')),
      when(
        pipe(find(propEq('type', 'MENTION')), propOr('', 'text'), (text: string) =>
          text.includes(`/members/${userId}`)
        ),
        pipe(
          find(propEq('type', 'URL')),
          propOr(null, 'text'),
          (url: string) => url && popupWindowService.openWebex(url)
        )
      )
    )(chunkMentionUrlText(log.text));
  };

  private scrollToBottom = ({ isUserLog, isScrollBottom, hasScrollbar, detailUiState }: LogCreateParams) => {
    if (uiState.hoverTriggerLog) {
      return;
    }
    this.setIgnoreUpdateScrollInfo(false, detailUiState);
    if (isUserLog || isScrollBottom || !hasScrollbar) {
      detailUiState.scrollToBottom();
    }
  };

  private animationEffect = ({ log }: LogCreateParams) => {
    isCherryBlossomText(log.text) && animationEffectStore.runAnimation(AnimationEffect.CherryBlossom);
  };

  private setLastLogSeq = ({ channel, hasFocus, channelLogsStore }: LogCreateParams) => {
    const channelId = channel?.channelId;
    if (!channelId) {
      return;
    }

    if (uiState.hasFocus && !hasFocus) {
      channelLogsStore.setLastReadLogSeq(channelId, channel.seq);
      uiState.setHasFocus(false);
    } else if (hasFocus) {
      uiState.setHasFocus(true);
    }
  };

  private appendLogOrFetchLogs = ({ channel, log, isUserLog, channelLogsStore, logActions }: LogCreateParams) => {
    if (!channel) {
      return;
    }
    const lastFetchedLogSeq = pipe<Log[], Log[], Log, number>(
      filter(prop('seq')),
      last,
      propOr(channel.startSeq, 'seq')
    )(channelLogsStore.logList);
    if (lastFetchedLogSeq >= channel.seq) {
      verifyRequiredProp('text', log);
      verifyRequiredProp('seq', log);
      verifyRequiredProp('senderId', log);
      this.removeDummyLog(log, channelLogsStore);
      channelLogsStore.setLog(log);
      return;
    }
    if (isUserLog) {
      return logActions.fetchLatestLogsIfNeeded(log.channelId);
    }
  };

  private readLog = ({ shouldRead, log }: LogCreateParams) => {
    if (!shouldRead) return;
    this.actions.proxyActions.updateReadSeq({
      channelId: log.channelId,
      readSeq: log.seq,
    });
  };

  private appendInviteLogInMain = (props: LogCreateParams) => {
    this.appendInviteLog({ mainChannel: props.channel, ...props });
  };

  private inviteOrAppendInviteLogInThread = (props: LogCreateParams) => {
    const { channel } = props;
    const mainChannel = channelManager.getMainChannel(channel?.channelId);
    this.appendInviteLog({ mainChannel, ...props });
  };

  private appendInviteLog = ({
    channel,
    mainChannel,
    log,
    isUserLog,
    detailUiState,
    channelLogsStore,
  }: LogCreateParams & { mainChannel: Channel }) => {
    if (!isUserLog || !channel || !mainChannel || !isPrivateChannel(mainChannel) || isForwardLog(log)) return;
    const { channelId } = channel;
    const { senderId, seq } = log;
    const inviteMembersId = without(
      mainChannel.members,
      getPreviewLog(log)
        .map((log) => extractMentionMembersId(log.text))
        .getOrElse([]) as MEMBER_ID[]
    );
    if (isEmpty(inviteMembersId)) return;
    pipe<MEMBER_ID[], InviteMemberLog[], void>(
      map(createInviteMemberLog({ channelId, senderId, seq }, log.id)),
      detailUiState.whenCurrentChannel(channelId, channelLogsStore.mergeLogs)
    )(inviteMembersId);
  };

  private calcContentsChannelNotInWork = ({ showCurrentChannelLog, log }: LogCreateParams) => {
    if (!showCurrentChannelLog || !isSystemLog(log)) {
      return;
    }
    safeParseJsonText(log.text).map(({ type }) => {
      if (type === SYSTEM_LOG_TYPE.JOIN_MEMBER) {
        this.actions.officeHoursActions.revalidateChannelNotInWork();
      }
    });
  };

  private openThreadInfoCoachMark = ({ log, isUserLog }: LogCreateParams) => {
    if (globalSettingStore.isMakeThreadComplete || !canCreateThreadLog(log) || isUserLog || uiState.hoverTriggerLog) {
      return;
    }
    this.actions.coachMarkActions.startMakeThreadCoachMark();
    uiState.setHoverTriggerLog(log.id);
  };

  private shouldShowNotification = ({ channel, log, isCurrentChannelLog, hasFocus, isUserLog }: LogCreateParams) => {
    const notification = globalSettingStore.notification;
    return (
      !isUserLog &&
      uiState.isMainView &&
      !isSystemLog(log) &&
      (!isCurrentChannelLog || !hasFocus) &&
      isNotificationOn(notification) &&
      channelManager.canNotification(channel)
    );
  };

  private getNotificationTitle = async ({ log, channel }) => {
    if (isNotificationNoneRange(globalSettingStore.notificationRange)) {
      return Promise.resolve(`${environment.serviceTitle} Messenger`);
    } else if (isThreadChannel(channel)) {
      const parentChannel = channelsStore.getById(channel.parentChannelId);
      const parentChannelTitle = await this.getNotificationTitle({ log, channel: parentChannel });
      return Promise.resolve(
        `${parentChannelTitle} | ${channel.title || i18n.t('logContent.deletedLog', '삭제된 메시지 입니다.')}`
      );
    } else if (isTopicChannel(channel) || channel.channelId === userStore.id) {
      return Promise.resolve(channel.title);
    } else if (isDirectChannel(channel) && !isCommandLog(log)) {
      return this.actions.memberActions.getOrFetchMemberById(log.senderId, channel.channelId).then(prop('name'));
    } else {
      return this.actions.channelMembersActions.getChannelMembersName(channel);
    }
  };

  private removeDummyLog = (log: Log, channelLogsStore: ChannelLogsStore) => {
    const { token } = log;
    if (isEmpty(token)) {
      channelLogsStore.removeAllDummyLogs();
    } else {
      const removeLogId = channelLogsStore.dummyLogTokenIdMap[token];
      removeLogId && channelLogsStore.removeLog(removeLogId);
    }
  };

  private getOrFetchChannelMailTitle = (channelId: string): Promise<string> => {
    return channelMailSettingStore.getOrFetch(channelId).then(this.getMailTitle(channelId));
  };

  private getSender = ({ channel, log }: { channel: Channel; log: Log }): Promise<string> => {
    if (
      isNotificationNoneRange(globalSettingStore.notificationRange) ||
      channel.channelId === userStore.id ||
      isDirectChannel(channel)
    ) {
      return Promise.resolve('');
    } else if (isMailLog(log)) {
      return this.getOrFetchChannelMailTitle(log.channelId);
    } else if (isCommandLog(log)) {
      return this.getOrFetchApp(log).then(prop('name'));
    } else if ('customName' in log && isNonEmpty(log.customName)) {
      return Promise.resolve(log.customName);
    } else {
      return this.actions.memberActions.getOrFetchMemberById(log.senderId, log.channelId).then(prop('name'));
    }
  };

  private getNotificationText = ({ log, channel }) => {
    if (!isNotificationAllRange(globalSettingStore.notificationRange)) {
      return isThreadChannel(channel)
        ? Promise.resolve(i18n.t('noti.receiveThread', '새 댓글이 도착했습니다.'))
        : Promise.resolve(i18n.t('noti.receiveLog', '새 메시지가 도착했습니다.'));
    } else if (isStickerLog(log)) {
      return Promise.resolve(i18n.t('noti.receiveSticker', '스티커를 전송했습니다.'));
    } else if (isVoIpLog(log)) {
      return pipe<any, any, any, any>(safeParseJsonText, mEither(always(''), getTextByVoIp), (text: string) =>
        Promise.resolve(text)
      )(log.text) as Promise<string>;
    } else if (isFileLog(log)) {
      return pipe<any, any, any>(
        cond([
          [isImageType, always(i18n.t('noti.receiveImage'))],
          [isVideo, always(i18n.t('noti.receiveVideo'))],
          [T, always(i18n.t('noti.receiveFile'))],
        ]),
        (text) => Promise.resolve(text)
      )(log.file);
    } else if (isForwardLog(log) || isReplyLog(log)) {
      return pipe<any, any, any, any>(
        getPreviewLog,
        map(objOf('log')),
        mMaybe(Promise.resolve(''), this.getNotificationText)
      )(log);
    }

    return Promise.resolve(replaceMentionToName(log.text));
  };

  private getOrFetchApp = (log: Log) => {
    const app = slashCommandAppsStore.getAppById(log.senderId);
    return app ? Promise.resolve(app) : this.fetchApp(log.senderId);
  };

  private fetchApp = (appId) => {
    return this.actions.slashCommandAppActions.fetchAppById(appId);
  };

  private updateChannelByDeleteLog = (log: Log) => {
    pipe<any, any, any, any>(
      pick(
        log.parentChannelId
          ? ['channelId', 'lastSeq', 'startSeq', 'unreadFlag']
          : ['channelId', 'lastSeq', 'startSeq', 'mentionCount', 'unreadCount']
      ),
      rename({ lastSeq: 'seq' }),
      channelManager.updateChannel
    )(log);
  };

  private deletePostedNotice = (log: Log) => {
    const { currentPostedNotice: notice } = channelNoticesStore;
    if (notice && notice.logId === log.id) {
      channelNoticesStore.removePostedNotice(log.channelId);
    }
  };

  private updatePostedNotice = (log: Log) => {
    const { currentPostedNotice: notice } = channelNoticesStore;
    if (notice && notice.logId === log.id) {
      getPreviewLog(log).map((fixedLog) => {
        channelNoticesStore.updatePostedNotice({
          channelId: log.channelId,
          notice: { text: fixedLog.text },
        });
      });
    }
  };

  private setIgnoreUpdateScrollInfo = (value: boolean, detailUiState: DetailUiState) => {
    detailUiState.setIgnoreUpdateScrollInfo(value);
  };
}

export default LogMessageHandler;
